import { FC, ReactElement } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { TFunction } from "i18next";
import {
  PackInstance,
  PackStatus,
} from "../../redux/actions/licenses/licenceTypes";
import { packsTooltipStyles } from "./licenseStyles/PacksTooltip";
import { packTooltipSlotProps } from "./licenseStyles/LicenceColumns.styles";

export const packStatusColor = (status: PackStatus): string => {
  switch (status) {
    case PackStatus.active:
      return "#2e7d32";
    case PackStatus.bound:
      return "#1565c0";
    case PackStatus.emptied:
      return "#ed6c02";
    case PackStatus.expired:
      return "#d32f2f";
    default:
      return "#9e9e9e";
  }
};

export const packStatusLabel = (status: PackStatus, t: TFunction): string => {
  switch (status) {
    case PackStatus.active:
      return t("licenses.active_pack");
    case PackStatus.bound:
      return t("licenses.pack_status_bound");
    case PackStatus.emptied:
      return t("licenses.pack_status_emptied");
    case PackStatus.expired:
      return t("licenses.pack_status_expired");
    default:
      return "Unbound";
  }
};

export const packStatusDesc = (status: PackStatus, t: TFunction): string => {
  switch (status) {
    case PackStatus.active:
      return t("licenses.pack_status_active_desc");
    case PackStatus.bound:
      return t("licenses.pack_status_bound_desc");
    case PackStatus.emptied:
      return t("licenses.pack_status_emptied_desc");
    case PackStatus.expired:
      return t("licenses.pack_status_expired_desc");
    default:
      return t("licenses.pack_status_unbound_desc");
  }
};

export type PacksTooltipCalculation = {
  rows: { label: string; value: number }[];
  totalLabel: string;
  total: number;
};

export const PacksTooltipContent: FC<{
  packs: PackInstance[];
  t: TFunction;
  language: string;
  calculation?: PacksTooltipCalculation;
}> = ({ packs, t, language, calculation }) => (
  <Box sx={packsTooltipStyles.container}>
    {packs.length > 0 && (
      <Typography variant="caption" sx={packsTooltipStyles.title}>
        {t("licenses.Packs")} ({packs.length})
      </Typography>
    )}
    <Box sx={packsTooltipStyles.list}>
      {[...packs]
        .sort(
          (a, b) =>
            new Date(a.activationDate).getTime() -
            new Date(b.activationDate).getTime(),
        )
        .map((pack, idx) => {
          const color = packStatusColor(pack.statusConstantId);
          return (
            <Box
              key={pack.id}
              sx={packsTooltipStyles.itemRow(idx === packs.length - 1)}
            >
              <Box sx={packsTooltipStyles.statusRow}>
                <Tooltip
                  title={packStatusDesc(pack.statusConstantId, t)}
                  placement="top"
                  arrow
                >
                  <Box sx={packsTooltipStyles.statusDot(color)} />
                </Tooltip>
                <Typography sx={packsTooltipStyles.statusLabel(color)}>
                  {packStatusLabel(pack.statusConstantId, t)}
                </Typography>
                <Typography sx={packsTooltipStyles.credits}>
                  {pack.initialCredits.toLocaleString()} cr.
                </Typography>
              </Box>
              <Typography sx={packsTooltipStyles.dates}>
                <Box component="span" sx={packsTooltipStyles.dateLabel}>
                  {t("licenses.pack_activation")}:
                </Box>{" "}
                {new Date(pack.activationDate).toLocaleString(language, {
                  dateStyle: "short",
                  timeStyle: "short",
                })}
                {"  ·  "}
                <Box component="span" sx={packsTooltipStyles.dateLabel}>
                  {t("licenses.pack_expiration")}:
                </Box>{" "}
                {new Date(pack.expiryDate).toLocaleDateString(language)}
              </Typography>
            </Box>
          );
        })}
    </Box>
    {calculation && (
      <Box sx={packsTooltipStyles.calcContainer(packs.length > 0)}>
        {calculation.rows.map((row) => (
          <Box key={row.label} sx={packsTooltipStyles.calcRow}>
            <Box component="span">{row.label}</Box>
            <Box component="span">
              {row.value < 0 ? "" : "+"}
              {row.value.toLocaleString()}
            </Box>
          </Box>
        ))}
        <Box sx={packsTooltipStyles.calcTotalRow}>
          <Box component="span">{calculation.totalLabel}</Box>
          <Box component="span">= {calculation.total.toLocaleString()}</Box>
        </Box>
      </Box>
    )}
  </Box>
);

export const PacksTooltip: FC<{
  packs: PackInstance[];
  t: TFunction;
  language: string;
  children: ReactElement;
  placement?: "bottom-start" | "top" | "top-start" | "bottom" | "right";
}> = ({ packs, t, language, children, placement = "bottom-start" }) => {
  if (!packs.length) return children;
  return (
    <Tooltip
      title={<PacksTooltipContent packs={packs} t={t} language={language} />}
      placement={placement}
      arrow
      slotProps={packTooltipSlotProps}
    >
      {children}
    </Tooltip>
  );
};
